import { Box, Typography, CircularProgress } from "@mui/material";
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { FC } from "react";
import { CustomDataGrid } from "../CustomDataGrid";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

type LpAccounts = {
  dealerName: string;
  lpSiteAddress: string;
  lpClientId: string;
};

const PRIMARY = "#1ba0e9";

const defaultColumnOptions: Partial<GridColDef> = {
  resizable: false,
  flex: 1,
  hideSortIcons: false,
  disableColumnMenu: false,
};

type LpMapGridProps = {
  accounts: LpAccounts[] | null;
  loading?: boolean;
};

export const LPMapGrid: FC<LpMapGridProps> = ({ accounts, loading = false }) => {
  const rows = accounts ?? [];

  const header = (title: string) => (
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "white" }}>
      {title}
    </Typography>
  );

  const columns: GridColDef[] = [
    {
      field: "dealerName",
      headerName: "Dealer Name",
      align: "left",
      renderHeader: () => header("Dealer Name"),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ fontSize: 14 }}>{params.value}</Typography>
        </Box>
      ),
      ...defaultColumnOptions,
    },
    {
      field: "lpSiteAddress",
      headerName: "LP URL",
      headerAlign: "center",
      align: "center",
      renderHeader: () => header("LP URL"),
      renderCell: (params: GridRenderCellParams) => {
        const url =
          typeof params.value === "string" && params.value.startsWith("http")
            ? params.value
            : `https://${params.value}`;

        return (
          <Box
            sx={{
              py: 1,
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
            }}
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          >
            <Typography
              sx={{
                fontSize: 14,
                color: PRIMARY,
                textDecoration: "underline",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              {params.value}
              <OpenInNewIcon sx={{ fontSize: 15, color: PRIMARY }} />
            </Typography>
          </Box>
        );
      },
      ...defaultColumnOptions,
    },
    {
      field: "lpClientId",
      headerName: "Client Id",
      headerAlign: "center",
      align: "center",
      renderHeader: () => header("Client Id"),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ fontSize: 14 }}>{params.value}</Typography>
        </Box>
      ),
      ...defaultColumnOptions,
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  return (
    <CustomDataGrid
      columns={columns}
      rows={rows}
      rowCount={rows.length}
      loading={loading}
      getRowId={(row) => row.lpClientId}
      sortingOrder={["asc", "desc"]}
    />
  );
};